import { defineStore } from "pinia";
import { ROLE_CODE, SYSTEM_TYPE, YES_OR_NO } from "@supplier/meta";
import { MainBody } from "@supplier/entity/src/entity/mainBody";

import type { Customer } from "@supplier/entity";

export interface UserInfo {
    /** 客户自身账号的客户信息 */
    customer: Customer;
    /** 客户权限 */
    permissions: number[];
    /** 客户代理账号的客户信息 */
    proxyAccount: Customer;
    /**
     * 系统类型 1-saas;2-独立部署
     */
    systemType: number;
    /**
     * 主体信息
     */
    mainBody: MainBody;
}

export interface UserStoreState {
    userInfo: UserInfo;
    zhihaoWebToken: string;
    mobile: string;
    isOldUser: number;
    roleCode: number;
    proxyAccountId: number;
    // 未读消息
    allUnreadMsgNum: number;
    /* 绑定时间 */
    bindTime: string;
    /* 微信昵称 */
    nickName: string;
    /* 电子签章授权状态 */
    authorizationState: number;
    /* 已阅读数据看板指引用户列表 */
    isReadDashboardGuideList: number[];
    /* 密码修改时间**/
    passwordUpdateTime: string;
    theme: {
        systemName: string | null;
        systemLogo: string | null;
    };
    needRefreshUserInfo: boolean;
}

export interface UserStoreGetters {
    /** 获取 token */
    token: string;
    /** 获取客户 ID */
    customerId: number | undefined;
    /** 是否是客户角色 */
    isCustomer: boolean;
    /** 是否是供应商角色 */
    isSupplier: boolean;
    /** 是否是供应链角色 */
    isSupplyChain: boolean;
    /** 是否已完成实名认证 */
    isCertified: boolean;
    /** 是否是 SAAS 用户 */
    isSAAS: boolean;
    /** 是否完成组织机构认证 */
    isEnterpriseAuth: boolean;
}

export interface UserStoreActions {
    setToken: (token: string) => void;
    setMobile: (mobile: string) => void;
    setUserInfo: (userInfo: UserInfo) => void;
    /** 设置绑定时间 */
    setBindTime: (bindTime: string) => void;
    /** 设置微信昵称 */
    setNickName: (nickName: string) => void;
    /** 设置电子签章授权状态 */
    setAuthorizationState: (authorizationState: number) => void;
    setProxyAccountId: (proxyAccountId: number) => void;
    setAllUnreadMsgNum: (num: number) => void;
    clear: () => void;
    /** 设置密码修改时间*/
    setPasswordUpdateTime: (time: string) => void;
    setSystemLogo: (systemLogoUrl: string | null) => void;
    setSystemName: (systemName: string | null) => void;
    setNeedRefreshUserInfo: (need: boolean) => void;
}

export type UserStore = UserStoreState & UserStoreGetters & UserStoreActions;

export const useUserStore = defineStore("zhongsheng-web-user", {
    state: (): UserStoreState => ({
        zhihaoWebToken: "",
        userInfo: {} as UserInfo,
        mobile: "",
        isOldUser: 0,
        roleCode: 0,
        proxyAccountId: 0,
        allUnreadMsgNum: 0,
        bindTime: "",
        nickName: "",
        authorizationState: 0,
        isReadDashboardGuideList: [],
        passwordUpdateTime: "",
        theme: {
            systemName: null,
            systemLogo: null
        },
        needRefreshUserInfo: false
    }),
    getters: {
        token: (state: UserStoreState) => {
            return state.zhihaoWebToken;
        },
        customerId: (state: UserStoreState) => {
            return state.userInfo?.customer.id;
        },
        isCustomer: (state: UserStoreState) => {
            return state.roleCode === ROLE_CODE.CUSTOMER.code;
        },
        isSupplier: (state: UserStoreState) => {
            return state.roleCode === ROLE_CODE.SUPPLIER.code;
        },
        isSupplyChain: (state: UserStoreState) => {
            return state.roleCode === ROLE_CODE.SUPPLY_CHAIN.code;
        },
        isCertified: (state: UserStoreState) => {
            return Boolean(state.userInfo?.customer.realName || state.userInfo?.customer.institutionName);
        },
        // 是否是SAAS用户
        isSAAS: (state: UserStoreState) => {
            return state.userInfo?.systemType === SYSTEM_TYPE.SAAS.code;
        },
        // 是否完成组织机构认证
        isEnterpriseAuth: (state: UserStoreState) => {
            return state.userInfo?.customer?.applyState === YES_OR_NO.YES.code;
        }
    },
    actions: {
        setToken(token: string) {
            this.zhihaoWebToken = token;
        },
        setMobile(mobile: string) {
            this.mobile = mobile;
        },
        setUserInfo(userInfo: UserInfo) {
            this.userInfo = userInfo;
        },
        /** 设置绑定时间 */
        setBindTime(bindTime: string) {
            this.bindTime = bindTime;
        },
        /** 设置微信昵称 */
        setNickName(nickName: string) {
            this.nickName = nickName;
        },
        /** 设置电子签章授权状态 */
        setAuthorizationState(authorizationState: number) {
            this.authorizationState = authorizationState;
        },
        setProxyAccountId(proxyAccountId: number) {
            this.proxyAccountId = proxyAccountId;
        },
        setAllUnreadMsgNum(num: number) {
            this.allUnreadMsgNum = num;
        },
        clear() {
            this.zhihaoWebToken = "";
            // this.userInfo = {};
            this.allUnreadMsgNum = 0;
            this.bindTime = "";
            this.nickName = "";
            this.authorizationState = 0;
        },
        /** 设置密码修改时间*/
        setPasswordUpdateTime(time: string) {
            this.passwordUpdateTime = time;
        },
        setSystemLogo(systemLogoUrl: string | null) {
            this.theme.systemLogo = systemLogoUrl;
        },
        setSystemName(systemName: string | null) {
            this.theme.systemName = systemName;
        },
        setNeedRefreshUserInfo(need: boolean) {
            this.needRefreshUserInfo = need;
        }
    } as UserStoreActions & ThisType<UserStore>,
    persist: {
        key: "zhongsheng-web-user",
        storage: sessionStorage,
        pick: ["zhihaoWebToken"]
    }
});
